
import LinkedTaskIcon from "@/components/alarm-queue/LinkedTaskIcon.vue";
import RegionalHandlerMixin from "@/mixins/RegionalHandlerMixin";
import api from "@/services/api.service";
import { Component, Vue, Mixins, Watch } from "vue-property-decorator";
import { EventRecord } from "@/store/site-monitor/types";
import { FilteredEvent, SystemEventRecord } from "@/store/eventqueue/types";
import { VueBootstrapField, UserPermissions } from "@/store/types";
import { get } from "lodash";
import { namespace, Getter } from "vuex-class";

const Eventqueue = namespace("eventqueue");

@Component({
	components: {
		"linked-task-icon": LinkedTaskIcon
	}
})
export default class SystemQueue extends Mixins(RegionalHandlerMixin) {
	@Eventqueue.Getter private getActiveEvents!: FilteredEvent[];
	@Eventqueue.Getter private getCountByType: (eventType: string) => number;
	@Eventqueue.Getter private getFilteredEventById: (id: number) => FilteredEvent;
	@Eventqueue.State(state => state.SystemEventRecords) private systemEventRecords: SystemEventRecord[];
	@Eventqueue.Action private getSystemEventRecords: (eventIds: number[]) => Promise<SystemEventRecord[]>;
	@Eventqueue.Mutation private removeSystemEventRecord: (eventRecords: number[]) => Promise<void>;
	@Eventqueue.Mutation private clearSystemEventRecords: () => Promise<void>;
	@Eventqueue.Mutation private removeFilteredEvents: (index: number) => void;
	@Getter private getPermissions: UserPermissions;

	private ackingAll: boolean = false;
	private ackingInProgress: number[] = [];
	private visible: boolean = false;

	private fields: VueBootstrapField[] = [
		{ key: "date", label: "Date", sortable: true, class: "date-col" },

		{ key: "details", label: "Alarm Details", sortable: false }
	];

	private get systemQueueCount(): number {
		const count =
			this.systemEvents && this.systemEvents.length
				? this.systemEvents.map((item: FilteredEvent) => item.alarmCount).reduce((prev, next) => prev + next)
				: 0;

		return count == 0 && this.systemEvents.length > 0 ? this.systemEvents.length : count;
	}

	private get systemEvents(): FilteredEvent[] {
		if (!this.getActiveEvents) return [];
		return this.getActiveEvents.filter((q: FilteredEvent) => q.eventTypeID == 1 && !q.groupID);
	}

	@Watch("systemQueueCount")
	private async onEventsUpdated(newCount: number, oldCount: number): Promise<void> {
		if (newCount > 0 && newCount != oldCount && this.visible) {
			await this.loadSystemEventDetails(true);
		}
	}

	private async loadSystemEventDetails(keepOpen: boolean = null): Promise<void> {
		if (!this.ackingAll) {
			this.visible = keepOpen ?? !this.visible;
			if (!this.visible || !this.systemEvents || (this.systemEvents && this.systemEvents.length == 0)) return;
			let eventIds = this.systemEvents.map((event: FilteredEvent) => event.eventID);

			await this.getSystemEventRecords(eventIds);
		}
	}

	private async ackAllEventRecords(): Promise<void> {
		// NB : implementation has been changed to ungroup system events, but endpoint supports future work so leaving this here as an example
		let records = []; //this.systemEventRecords.map(m => m.eventRecordIds).flat(1);
		let systemEvents = this.systemEvents.map(e => e.eventID);
		await this.ackEventRecords(records, systemEvents, true);
	}

	/**
	 * Mark event records as processed, and param to close the event
	 */
	private async ackEventRecords(
		eventRecords: number[],
		eventIds: number[],
		closeEvent: boolean | undefined
	): Promise<void> {
		// show loading spinners
		this.ackingAll = closeEvent;

		// array of items that are currently being acked, to show loading icons
		this.ackingInProgress = eventRecords;

		// send request
		const response = await api.ackSystemEvent(eventRecords, eventIds, closeEvent);
		const success: boolean = response.data;

		if (success) {
			// reset flags
			this.ackingAll = false;
			this.ackingInProgress = [];

			this.removeSystemEventRecord(eventRecords);

			// if we are closing the event, update the store
			if (closeEvent) {
				this.removeFilteredEventsFromStore(this.systemEvents);
				this.clearSystemEventRecords();
			}
		}
	}

	/**
	 * Permission and feature check
	 */
	public get canViewSystemEvents(): boolean {
		return (
			get(this.featuresList, ["Alarms", "EventQueue", "SystemEvents"]) &&
			(this.getPermissions.isAdmin || this.getPermissions.canViewSystemEvents)
		);
	}

	private removeFilteredEventsFromStore(events: FilteredEvent[]): void {
		events.forEach(event => {
			const systemEvent = this.getFilteredEventById(event.eventID);
			const index = systemEvent ? this.systemEvents.indexOf(systemEvent) : null;
			if (index) {
				this.removeFilteredEvents(index);
			}
		});
	}
}
